import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { ChevronLeft, ChevronRight, Phone, CheckCircle, Calendar } from 'lucide-react';

const STATUS_OPTIONS = ['new','contacted','interested','not_interested','callback','not_picked','won','lost'];
const STATUS_COLORS = {
  new: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  contacted: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
  interested: 'bg-green-500/10 text-green-400 border-green-500/20',
  not_interested: 'bg-red-500/10 text-red-400 border-red-500/20',
  callback: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  not_picked: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  won: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  lost: 'bg-gray-500/10 text-gray-400 border-gray-500/20',
};

export default function Pipeline() {
  const { user } = useAuth();
  const today = new Date().toISOString().split('T')[0];
  const [selectedDate, setSelectedDate] = useState(today);
  const [cards, setCards] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [dailyRes, summaryRes] = await Promise.all([
        api.get('/api/pipeline/daily?date=' + selectedDate),
        api.get('/api/pipeline/status-summary')
      ]);
      setCards(dailyRes.data.cards || []);
      setSummary(summaryRes.data || {});
    } catch(e) { console.error(e); }
    setLoading(false);
  }, [selectedDate]);

  useEffect(() => { loadData(); }, [loadData]);

  const changeDate = (days) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + days);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  const markDone = async (reminderId) => {
    try { await api.put('/api/pipeline/reminders/' + reminderId + '/done'); loadData(); } catch(e) {}
  };

  const changeStatus = async (leadId, status) => {
    try { await api.put('/api/leads/' + leadId, { status }); loadData(); } catch(e) {}
  };

  return (
    <div style={{padding:'16px'}}>
      <div style={{marginBottom:'24px'}}>
        <h3 style={{color:'white',fontSize:'20px',fontWeight:'bold',margin:0}}>Pipeline</h3>
        <p style={{color:'#6b7280',fontSize:'14px',margin:'4px 0 0 0'}}>Track your leads and daily contacts</p>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'12px',marginBottom:'24px'}}>
        {STATUS_OPTIONS.map(s => (
          <div key={s} style={{background:'rgba(255,255,255,0.05)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'12px',padding:'12px'}}>
            <p style={{color:'#9ca3af',fontSize:'11px',margin:'0 0 4px 0',textTransform:'capitalize'}}>{s.replace(/_/g,' ')}</p>
            <p style={{color:'white',fontSize:'24px',fontWeight:'bold',margin:0}}>{summary[s] || 0}</p>
          </div>
        ))}
      </div>

      <div style={{background:'rgba(255,255,255,0.05)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'12px',padding:'16px'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'16px'}}>
          <h4 style={{color:'white',fontWeight:'600',margin:0,display:'flex',alignItems:'center',gap:'8px'}}>
            Daily Contact Cards
          </h4>
          <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
            <button onClick={() => changeDate(-1)} style={{background:'rgba(255,255,255,0.1)',border:'none',borderRadius:'8px',padding:'6px',cursor:'pointer',color:'white'}}>&#8249;</button>
            <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)}
              style={{background:'rgba(255,255,255,0.1)',border:'1px solid rgba(255,255,255,0.2)',borderRadius:'8px',padding:'6px 12px',color:'white',fontSize:'14px'}} />
            {selectedDate !== today && <button onClick={() => setSelectedDate(today)} style={{background:'rgba(212,160,23,0.1)',border:'none',borderRadius:'8px',padding:'6px 12px',color:'#d4a017',cursor:'pointer',fontSize:'12px'}}>Today</button>}
            <button onClick={() => changeDate(1)} style={{background:'rgba(255,255,255,0.1)',border:'none',borderRadius:'8px',padding:'6px',cursor:'pointer',color:'white'}}>&#8250;</button>
          </div>
        </div>

        {loading ? (
          <div style={{textAlign:'center',padding:'32px',color:'#9ca3af'}}>Loading...</div>
        ) : cards.length === 0 ? (
          <div style={{textAlign:'center',padding:'32px',color:'#6b7280'}}>No contacts scheduled for this day.<br/><span style={{fontSize:'12px'}}>Add reminders to leads to see them here.</span></div>
        ) : (
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:'12px'}}>
            {cards.map(card => {
              const lead = card.lead;
              const isDone = card.is_done;
              const isWon = lead.status === 'won';
              return (
                <div key={card.reminder_id} style={{
                  background: isDone ? 'rgba(255,255,255,0.02)' : isWon ? 'rgba(16,185,129,0.05)' : 'rgba(255,255,255,0.07)',
                  border: `1px solid ${isDone ? 'rgba(255,255,255,0.05)' : isWon ? 'rgba(16,185,129,0.2)' : 'rgba(255,255,255,0.1)'}`,
                  borderRadius:'12px', padding:'14px', opacity: isDone ? 0.5 : 1
                }}>
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:'8px'}}>
                    <div>
                      <p style={{color:'white',fontWeight:'500',fontSize:'14px',margin:0}}>{lead.name}</p>
                      <p style={{color:'#9ca3af',fontSize:'12px',margin:'2px 0 0 0'}}>{lead.phone}</p>
                    </div>
                    <span style={{padding:'2px 8px',borderRadius:'9999px',fontSize:'11px',background:'rgba(255,255,255,0.05)',color:'#9ca3af',height:'fit-content'}}>
                      {lead.status?.replace(/_/g,' ')}
                    </span>
                  </div>

                  {card.reminder_note && <p style={{color:'#d1d5db',fontSize:'12px',fontStyle:'italic',margin:'0 0 10px 0'}}>"{card.reminder_note}"</p>}

                  {isWon && <div style={{background:'rgba(16,185,129,0.1)',color:'#34d399',borderRadius:'8px',padding:'4px',textAlign:'center',fontSize:'12px',marginBottom:'10px'}}>🏆 Deal Won</div>}

                  {!isDone && !isWon && (
                    <select value={lead.status} onChange={e => changeStatus(lead.id, e.target.value)}
                      style={{width:'100%',background:'rgba(255,255,255,0.05)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'8px',padding:'6px',color:'white',fontSize:'12px',marginBottom:'10px'}}>
                      {STATUS_OPTIONS.map(s => <option key={s} value={s} style={{background:'#1f2937'}}>{s.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase())}</option>)}
                    </select>
                  )}

                  <div style={{display:'flex',gap:'8px'}}>
                    {lead.phone && !isDone && (
                      <a href={'tel:' + lead.phone} style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',gap:'4px',padding:'6px',background:'rgba(16,185,129,0.1)',color:'#34d399',borderRadius:'8px',fontSize:'12px',textDecoration:'none'}}>
                        📞 Call
                      </a>
                    )}
                    {!isDone && (
                      <button onClick={() => markDone(card.reminder_id)} style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',gap:'4px',padding:'6px',background:'rgba(212,160,23,0.1)',color:'#d4a017',border:'none',borderRadius:'8px',fontSize:'12px',cursor:'pointer'}}>
                        ✓ Done
                      </button>
                    )}
                    {isDone && <p style={{color:'#6b7280',fontSize:'12px',textAlign:'center',width:'100%',padding:'6px 0',margin:0}}>✓ Completed</p>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
